<template>
  <!-- Follows the report out: opens with a spinner the moment it is sent, turns into a check once Sentry
       has accepted it, then says what happens next and stays until it is closed. Sentry's own pill, gone
       after five seconds, left people unsure whether a report had arrived. Persistent while sending: a
       failure closes it by itself and hands back to the form, which shows why. `sentry-unmask`: every
       word here is ours, so a replay may show it. -->
  <lew-modal-card
    class="sentry-unmask"
    :model-value="Boolean(feedbackReport)"
    :max-width="560"
    :persistent="phase !== 'text'"
    :hide-actions="phase !== 'text'"
    :title="phase === 'sending' ? $t('general.sentry.sent.sending') : $t('general.sentry.sent.title')"
    :subtitle="phase === 'text' ? $t('general.sentry.sent.reference', { reference }) : undefined"
    @update:model-value="open => !open && close()"
  >
    <div class="sent-dialog__body">
      <div class="sent-dialog__status">
        <v-progress-circular v-if="phase === 'sending'" indeterminate color="success" size="64" width="4" />
        <svg v-else class="sent-dialog__check" viewBox="0 0 64 64" aria-hidden="true">
          <circle class="sent-dialog__circle" cx="32" cy="32" r="30" />
          <path class="sent-dialog__mark" d="M20 33l8 8 16-17" />
        </svg>
      </div>
      <v-expand-transition>
        <div v-if="phase === 'text'" class="sent-dialog__text">
          {{ $t("general.sentry.sent.text") }} {{ $t("general.sentry.sent.reply") }}
        </div>
      </v-expand-transition>
    </div>

    <template #actions>
      <!-- LewFormDialog's submit button. -->
      <v-btn variant="flat" color="primary" rounded="lg" class="text-none font-weight-bold px-6" @click="close">
        {{ $t("general.close") }}
      </v-btn>
    </template>
  </lew-modal-card>
</template>

<script setup lang="ts">
/**
 * The bug report on its way and its arrival. The state is set by `plugins/sentryFeedbackConfirmation.js`;
 * this is its only renderer, mounted once in App.vue.
 */
import { onUnmounted, ref, watch } from "vue"
import LewModalCard from "@/components/general/LewModalCard/LewModalCard.vue"
import { feedbackReport, sendingRemainder } from "@/plugins/sentryFeedbackConfirmation"

/** The circle and the check being drawn, see the animations below. */
const CHECK_MS = 800

const phase = ref<"sending" | "check" | "text">("sending")
// Kept apart from the state, so the subtitle does not empty while the dialog fades out.
const reference = ref("")
let timer: ReturnType<typeof setTimeout> | undefined

function after(ms: number, step: () => void) {
  clearTimeout(timer)
  timer = setTimeout(step, ms)
}

// Every assignment is a new object, so a second report starts over even while the first is still shown.
watch(feedbackReport, report => {
  clearTimeout(timer)
  if (!report) return
  reference.value = report.reference
  if (report.status === "sending") {
    phase.value = "sending"
  } else {
    after(sendingRemainder(), () => {
      phase.value = "check"
      // Without motion the check is there at once (see the media query below).
      const still = matchMedia("(prefers-reduced-motion: reduce)").matches
      after(still ? 0 : CHECK_MS, () => (phase.value = "text"))
    })
  }
})

onUnmounted(() => clearTimeout(timer))

function close() {
  feedbackReport.value = null
}
</script>

<style scoped>
.sent-dialog__body {
  padding: 32px 24px 24px;
  text-align: center;
}

.sent-dialog__status {
  display: flex;
  justify-content: center;
  height: 64px;
}

.sent-dialog__check {
  width: 64px;
  height: 64px;
}

.sent-dialog__circle,
.sent-dialog__mark {
  fill: none;
  stroke: rgb(var(--v-theme-success));
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Drawn from the top, where the spinner's arc was, then the check inside it. */
.sent-dialog__circle {
  stroke-dasharray: 189;
  stroke-dashoffset: 189;
  transform: rotate(-90deg);
  transform-origin: center;
  animation: sent-dialog-draw 0.45s ease-out forwards;
}

.sent-dialog__mark {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: sent-dialog-draw 0.3s ease-out 0.4s forwards;
}

@keyframes sent-dialog-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sent-dialog__circle,
  .sent-dialog__mark {
    animation: none;
    stroke-dashoffset: 0;
  }
}

/* Padding, not margin: the expand transition measures the element, not the space above it. */
.sent-dialog__text {
  padding-top: 20px;
  line-height: 1.6;
}
</style>
